import React, { useState, useMemo } from "react";
import {
  ViewIcon,
  CloseIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  ClockIcon,
  CalendarIcon,
  SearchIcon,
  ChevronDownIcon,
} from "../../../Icons";
import Table from "../../../components/UI/Table";
import { displayDateUS, isDateExpired } from "../../../utils/dateUtils";
import { CELL_MUTED, CELL_STRONG, MODULE_TAB_BTN } from "../../../ui/pageChrome";
import {
  REPORT_THEME,
  REPORT_BTN,
  REPORT_FIELD,
  REPORT_MODAL,
  REPORT_PROGRESS,
  REPORT_ROLE_CHIP,
} from "../reportTheme";

const ProgressBar = ({ current, total, colorClass, label }) => {
  const percentage =
    total > 0 ? Math.min((current / total) * 100, 100) : current > 0 ? 100 : 0;

  return (
    <div className="w-full min-w-[120px] max-w-[140px]">
      <div className="flex items-baseline justify-between gap-1 mb-0.5">
        <span className="text-[11px] font-semibold text-slate-800 tabular-nums">
          {Math.round(percentage)}%
        </span>
        <span className="text-[10px] text-slate-500 tabular-nums truncate">
          {current}/{total} {label || "done"}
        </span>
      </div>
      <div className="w-full bg-slate-100 rounded-full h-1.5">
        <div
          className={`h-1.5 rounded-full ${colorClass}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const DetailsModal = ({ employee, onClose }) => {
  const [activeTab, setActiveTab] = useState("application");

  if (!employee) return null;

  const renderFormList = (forms, type) => {
    const columns = [
      {
        header: "Form Name",
        accessor: "name",
        className: "min-w-0 pl-3 pr-2 align-middle",
        render: (form) => (
          <div className="flex items-center gap-2 min-w-0">
            {form.status === "Filled" ? (
              <CheckCircleIcon size={15} className="text-emerald-500 shrink-0" />
            ) : form.status === "Missing" ? (
              <AlertCircleIcon size={15} className="text-red-500 shrink-0" />
            ) : (
              <ClockIcon size={15} className="text-amber-500 shrink-0" />
            )}
            <span className={`${CELL_STRONG} truncate text-slate-900`}>
              {form.name}
            </span>
          </div>
        ),
      },
      {
        header: "Status",
        accessor: "status",
        className: "whitespace-nowrap px-2 align-middle",
        render: (form) => (
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold ${
              form.status === "Filled"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : form.status === "Missing"
                  ? "bg-red-50 text-red-700 border-red-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
            }`}
          >
            {form.status}
          </span>
        ),
      },
      {
        header: type === "credentials" ? "Expiry Date" : "Submitted On",
        className: "whitespace-nowrap px-2 align-middle",
        render: (form) => {
          if (type === "application" || type === "medical") {
            return form.submittedAt ? (
              <span
                className={`${CELL_MUTED} inline-flex items-center gap-1 tabular-nums`}
              >
                <CalendarIcon size={12} />
                {displayDateUS(form.submittedAt)}
              </span>
            ) : (
              <span className={CELL_MUTED}>—</span>
            );
          }
          return (type === "credentials" || type === "tax") &&
            form.uploads?.length > 0 ? (
            <span
              className={`inline-flex items-center gap-1 tabular-nums text-[13px] ${
                type === "credentials" &&
                isDateExpired(form.uploads[0]?.expireAt)
                  ? "text-red-600 font-medium"
                  : "text-slate-600"
              }`}
            >
              <CalendarIcon size={12} />
              {displayDateUS(form.uploads[0]?.expireAt)}
              {type === "credentials" &&
              isDateExpired(form.uploads[0]?.expireAt) ? (
                <span className="inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold bg-red-50 text-red-700 border-red-200">
                  Expired
                </span>
              ) : null}
            </span>
          ) : (
            <span className={CELL_MUTED}>—</span>
          );
        },
      },
    ];

    if (!forms || forms.length === 0) {
      return (
        <div className="h-full flex flex-col items-center justify-center text-slate-500 py-12">
          <p className="text-sm font-medium text-slate-600">No records found</p>
          <p className="text-xs text-slate-400 mt-1">
            There are no records available for this category.
          </p>
        </div>
      );
    }

    return (
      <div
        className={`m-3 bg-white rounded-xl border overflow-hidden ${REPORT_THEME.section.wrap}`}
      >
        <Table
          columns={columns}
          data={forms}
          loading={false}
          showSrNo={false}
          density="compact"
          embedded
          headTone={REPORT_THEME.tableHead}
        />
      </div>
    );
  };

  const tabs = [
    { id: "application", label: "Application" },
    { id: "medical", label: "Medical" },
    { id: "credentials", label: "Credentials" },
    { id: "tax", label: "Tax" },
  ];

  return (
    <div
      className={REPORT_MODAL.backdrop}
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className={REPORT_MODAL.panel}>
        <div className={REPORT_MODAL.head}>
          <div className="min-w-0">
            <h2 className={REPORT_MODAL.title}>{employee.employeeName}</h2>
            <p className={REPORT_MODAL.subtitle}>
              Role: {employee.role || "—"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={REPORT_BTN.modalClose}
            title="Close"
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div
          className={`grid grid-cols-4 w-full border-b shrink-0 ${REPORT_THEME.section.head}`}
        >
          {tabs.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                className={`${MODULE_TAB_BTN} border-indigo-100 ${
                  active
                    ? REPORT_THEME.tab.active
                    : REPORT_THEME.tab.inactive
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
                {active ? (
                  <span
                    className={`absolute left-0 right-0 bottom-0 h-0.5 ${REPORT_THEME.tab.underline}`}
                  />
                ) : null}
              </button>
            );
          })}
        </div>

        <div className={REPORT_MODAL.body}>
          {activeTab === "application"
            ? renderFormList(employee.applicationForms?.forms, "application")
            : null}
          {activeTab === "medical"
            ? renderFormList(employee.medicalForms?.forms, "medical")
            : null}
          {activeTab === "credentials"
            ? renderFormList(
                employee.credentialsForUpload?.forms,
                "credentials",
              )
            : null}
          {activeTab === "tax"
            ? renderFormList(employee.taxForms?.forms, "tax")
            : null}
        </div>
      </div>
    </div>
  );
};

const EmployeeComplianceTable = ({
  data,
  page,
  totalPages,
  onPageChange,
  loading,
  totalItems,
  itemsPerPage,
}) => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const roleOptions = useMemo(() => {
    const roles = new Set();
    (data?.items || []).forEach((row) => {
      if (row.role) roles.add(String(row.role));
    });
    return [
      { value: "", label: "All Roles" },
      ...Array.from(roles)
        .sort((a, b) => a.localeCompare(b))
        .map((role) => ({ value: role, label: role })),
    ];
  }, [data?.items]);

  const hasActiveFilters = Boolean(search.trim() || roleFilter);

  const clearFilters = () => {
    setSearch("");
    setRoleFilter("");
  };

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.items || []).filter((row) => {
      if (roleFilter && String(row.role || "") !== roleFilter) return false;
      if (!q) return true;
      const name = String(row.employeeName || "").toLowerCase();
      const role = String(row.role || "").toLowerCase();
      return name.includes(q) || role.includes(q);
    });
  }, [data?.items, search, roleFilter]);

  const columns = useMemo(
    () => [
      {
        header: "Employee",
        accessor: "employeeName",
        className: "min-w-[9rem] px-2 align-middle",
        render: (row) => (
          <div
            className={`${CELL_STRONG} capitalize text-slate-900 truncate`}
            title={row.employeeName || ""}
          >
            {row.employeeName || "—"}
          </div>
        ),
      },
      {
        header: "Role",
        accessor: "role",
        className: "whitespace-nowrap px-2 align-middle w-[4.5rem]",
        render: (row) => (
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wide border ${REPORT_ROLE_CHIP}`}
          >
            {row.role || "—"}
          </span>
        ),
      },
      {
        header: "Application Forms",
        accessor: "applicationForms",
        className: "px-2 align-middle min-w-[7rem]",
        render: (row) => {
          const filled = row.applicationForms?.filledCount || 0;
          const missing = row.applicationForms?.missingCount || 0;
          return (
            <ProgressBar
              current={filled}
              total={filled + missing}
              colorClass={REPORT_PROGRESS}
            />
          );
        },
      },
      {
        header: "Medical Forms",
        accessor: "medicalForms",
        className: "px-2 align-middle min-w-[7rem]",
        render: (row) => {
          const filled = row.medicalForms?.filledCount || 0;
          const missing = row.medicalForms?.missingCount || 0;
          return (
            <ProgressBar
              current={filled}
              total={filled + missing}
              colorClass={REPORT_PROGRESS}
            />
          );
        },
      },
      {
        header: "Credentials",
        accessor: "credentialsForUpload",
        className: "px-2 align-middle min-w-[7rem]",
        render: (row) => {
          const uploaded = row.credentialsForUpload?.uploadedCount || 0;
          const missing = row.credentialsForUpload?.missingCount || 0;
          return (
            <ProgressBar
              current={uploaded}
              total={uploaded + missing}
              colorClass={REPORT_PROGRESS}
              label="up"
            />
          );
        },
      },
      {
        header: "Tax Forms",
        accessor: "taxForms",
        className: "px-2 align-middle min-w-[7rem]",
        render: (row) => {
          const uploaded = row.taxForms?.filledCount || 0;
          const missing = row.taxForms?.missingCount || 0;
          return (
            <ProgressBar
              current={uploaded}
              total={uploaded + missing}
              colorClass={REPORT_PROGRESS}
              label="up"
            />
          );
        },
      },
      {
        header: "Actions",
        accessor: "actions",
        className: "whitespace-nowrap pl-2 pr-3 text-right align-middle w-[5.5rem]",
        render: (row) => (
          <div className="inline-flex justify-end">
            <button
              type="button"
              onClick={() => setSelectedEmployee(row)}
              className={REPORT_BTN.soft}
              title="View details"
              aria-label={`View details for ${row.employeeName || "employee"}`}
            >
              <ViewIcon size={13} />
              View
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 px-3 py-2.5 border-b border-indigo-100">
        <div className="relative flex-1 min-w-0">
          <SearchIcon
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search by name, role..."
            className={REPORT_FIELD.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
              aria-label="Clear search"
            >
              <CloseIcon size={14} />
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <div className="relative">
            <label className="sr-only" htmlFor="compliance-role-filter">
              Filter by role
            </label>
            <select
              id="compliance-role-filter"
              className={`${REPORT_FIELD.select} ${
                roleFilter ? REPORT_FIELD.selectActive : ""
              }`}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              {roleOptions.map((option) => (
                <option key={option.value || "all-roles"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDownIcon
              size={14}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
          </div>

          {hasActiveFilters ? (
            <button
              type="button"
              onClick={clearFilters}
              className={REPORT_BTN.clear}
            >
              Clear
            </button>
          ) : null}

          <span
            className={`h-8 inline-flex items-center px-2.5 rounded-md text-[11px] font-semibold tabular-nums ${REPORT_THEME.section.badge}`}
          >
            {filteredItems.length}
            {hasActiveFilters ? ` / ${data?.items?.length || 0}` : ""} employees
          </span>
        </div>
      </div>

      <Table
        columns={columns}
        data={filteredItems}
        loading={loading}
        keyField="employeeId"
        showSrNo
        density="compact"
        embedded
        tableClassName="table-fixed"
        srNoClassName="w-10 whitespace-nowrap pl-3 pr-1 text-center align-middle"
        headTone={REPORT_THEME.tableHead}
        pagination={{
          currentPage: page,
          totalPages: totalPages,
          onPageChange: onPageChange,
          totalItems: totalItems,
          itemsPerPage: itemsPerPage,
        }}
        noDataMessage="No employees found."
      />

      {selectedEmployee ? (
        <DetailsModal
          employee={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
        />
      ) : null}
    </>
  );
};

export default EmployeeComplianceTable;
